// Who is signed in, and which workspace they are working in.
//
// Two separate things deliberately kept in one provider, because every page
// needs both and because the second is derived from the first: `GET /api/me`
// is the only thing that knows which tenants a uid belongs to, and a Firebase
// user with no tenant yet is a real state -- it is what every brand-new account
// looks like for the moment between signing up and the first tenant being
// created.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  onIdTokenChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile,
  type User
} from "firebase/auth";
import { firebaseAuth, initFirebase } from "../lib/firebase";
import * as api from "../lib/api";
import type { Me, Tenant } from "../lib/types";
import { arrivingOnInvite, markSignedOut } from "../lib/returnTo";
import { track } from "../lib/analytics";

type AuthState = {
  // null once we know nobody is signed in; undefined while we do not yet know.
  user: User | null | undefined;
  me: Me | null;
  tenant: Tenant | null;
  loading: boolean;
  error: string | null;
  // Set only when Firebase itself could not start -- no config, no network.
  // Distinct from `error`, which is a problem with this account rather than
  // with the app, and distinct from `user === undefined`, which resolves.
  startupError: string | null;
  signInWithGoogle: () => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signUpWithPassword: (email: string, password: string, displayName: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshMe: () => Promise<Me | null>;
  // Move to another account this person belongs to.
  switchTenant: (tenantId: string) => void;
};

const AuthContext = createContext<AuthState | null>(null);

export const TENANT_KEY = "buildbotsnow.tenantId";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [me, setMe] = useState<Me | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(() =>
    // Only a preference, never a permission: the server checks membership on
    // every call, so the worst a tampered value can do is show an error.
    typeof localStorage === "undefined" ? null : localStorage.getItem(TENANT_KEY)
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startupError, setStartupError] = useState<string | null>(null);

  // Guards the first-tenant bootstrap below. Without it, two renders racing
  // each other would create two workspaces for one new account.
  const creatingTenant = useRef(false);

  const refreshMe = useCallback(async () => {
    const profile = await api.getMe();
    setMe(profile);
    return profile;
  }, []);

  // Starting Firebase used to happen in main.tsx, with nothing rendered until
  // it resolved. That was fine when every page needed auth; it stopped being
  // fine when "/" became a marketing page, because it meant a first-time
  // visitor got a white screen for the length of a config fetch plus an
  // IndexedDB read, and a blunt "could not start" card if either failed. So it
  // happens here instead, with the tree already on screen.
  useEffect(() => {
    let canceled = false;
    let unsubscribe: (() => void) | undefined;

    initFirebase()
      .then((auth) => {
        if (canceled) return;
        // onIdTokenChanged rather than onAuthStateChanged: it also fires on
        // token refresh, which is when a just-verified email or a changed
        // display name becomes visible to the backend.
        unsubscribe = onIdTokenChanged(auth, (nextUser) => {
          setUser(nextUser);
          if (!nextUser) {
            setMe(null);
            setLoading(false);
          }
        });
      })
      .catch((err: Error) => {
        if (canceled) return;
        setStartupError(err.message);
        setLoading(false);
      });

    return () => {
      canceled = true;
      unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    if (user === undefined) return;
    if (user === null) return;

    let canceled = false;
    setLoading(true);

    (async () => {
      try {
        let profile = await refreshMe();

        // A brand-new account has no workspace. Creating it here rather than
        // asking the user to name one first is a deliberate choice: the name is
        // editable later and nobody signs up wanting to fill in a form about
        // organizational structure.
        //
        // Except for someone arriving on a team invite: they are joining an
        // account, and a workspace of their own would be an empty second one.
        // If the invite fails, the page sends them to /agents with a reload,
        // which runs this again without the exception.
        if (profile.tenants.length === 0 && !creatingTenant.current && !arrivingOnInvite()) {
          creatingTenant.current = true;
          try {
            await api.createTenant(profile.displayName || profile.email || "My workspace");
            profile = await refreshMe();
          } finally {
            creatingTenant.current = false;
          }
        }

        if (!canceled) setError(null);
      } catch (err) {
        if (!canceled) setError(messageFor(err));
      } finally {
        if (!canceled) setLoading(false);
      }
    })();

    return () => {
      canceled = true;
    };
  }, [user, refreshMe]);

  // The remembered tenant, but only if the server still says they belong to it.
  const tenant = useMemo(() => {
    if (!me || me.tenants.length === 0) return null;
    return me.tenants.find((t) => t.tenantId === tenantId) || me.tenants[0];
  }, [me, tenantId]);

  useEffect(() => {
    if (tenant && tenant.tenantId !== tenantId) {
      setTenantId(tenant.tenantId);
      localStorage.setItem(TENANT_KEY, tenant.tenantId);
    }
  }, [tenant, tenantId]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      me,
      tenant,
      loading,
      error,
      startupError,
      async signInWithGoogle() {
        const provider = new GoogleAuthProvider();
        // Always show the chooser. Someone with two Google accounts who lands
        // in the wrong workspace has no obvious way back otherwise.
        provider.setCustomParameters({ prompt: "select_account" });
        const credential = await signInWithPopup(firebaseAuth(), provider);
        // A first Google sign-in is a sign-up (lib/analytics.ts).
        track(getAdditionalUserInfo(credential)?.isNewUser ? "sign_up" : "login", { method: "google" });
      },
      async signInWithPassword(email, password) {
        await signInWithEmailAndPassword(firebaseAuth(), email.trim(), password);
        track("login", { method: "password" });
      },
      async signUpWithPassword(email, password, displayName) {
        const credential = await createUserWithEmailAndPassword(
          firebaseAuth(),
          email.trim(),
          password
        );
        track("sign_up", { method: "password" });
        // Email/password accounts arrive with no display name at all, so the
        // dashboard would greet them as their own email address forever.
        if (displayName.trim()) {
          await updateProfile(credential.user, { displayName: displayName.trim() });
        }
      },
      async resetPassword(email) {
        await sendPasswordResetEmail(firebaseAuth(), email.trim());
      },
      async signOut() {
        // Before the session ends: the page re-renders signed out at once.
        markSignedOut(`${window.location.pathname}${window.location.search}`);
        await firebaseSignOut(firebaseAuth());
        localStorage.removeItem(TENANT_KEY);
        setMe(null);
      },
      refreshMe,
      switchTenant(next) {
        setTenantId(next);
        localStorage.setItem(TENANT_KEY, next);
      }
    }),
    [user, me, tenant, loading, error, startupError, refreshMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Whether this browser has ever finished signing in here.
//
// A hint for what to paint first, nothing more: it grants no access, the server
// checks every request regardless, and the worst a stale value can do is cost
// one wrong first frame. It reads the remembered tenant because that key is
// written as soon as a session has a workspace and removed on sign-out.
export function hasSignedInBefore(): boolean {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(TENANT_KEY) !== null;
  } catch {
    // Safari in private mode throws on localStorage rather than returning null.
    return false;
  }
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside <AuthProvider>");
  return context;
}

// Firebase Auth errors carry a machine code and a message built for developers
// ("Firebase: Error (auth/wrong-password)."). Translating the handful a user
// can actually cause is the difference between a login form that helps and one
// that shows people a stack-trace fragment.
export function messageFor(err: unknown): string {
  const code = (err as { code?: string })?.code || "";
  switch (code) {
    case "auth/invalid-email":
      return "That doesn't look like an email address.";
    case "auth/user-disabled":
      return "This account has been disabled.";
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "That email and password don't match an account.";
    case "auth/email-already-in-use":
      return "There's already an account with that email. Try signing in instead.";
    case "auth/weak-password":
      return "Passwords need to be at least 6 characters.";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "Sign-in was canceled.";
    case "auth/popup-blocked":
      return "Your browser blocked the sign-in window. Allow pop-ups for this site and try again.";
    case "auth/too-many-requests":
      return "Too many attempts. Wait a few minutes and try again.";
    case "auth/network-request-failed":
      return "Could not reach the server. Check your connection and try again.";
    default:
      return (err as Error)?.message || "Something went wrong. Please try again.";
  }
}
